import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { withoutQueryParams } from "@app/common/utils";

import Alert from "@rui/Alert";

interface MessageConfig {
  error: { [key: string]: string };
  success: { [key: string]: string };
}

type UpdateSource = "subscription_purchase" | "credit_purchase" | "update";

const PAYMENT_FAILED_ERROR_MESSAGE =
  "Payment failed. Please review your billing details or try again with a different payment method.";

const SUBSCRIPTION_PURCHASE_MESSAGE_BY_OPTS: MessageConfig = {
  success: {
    default: "Your Read plan has been successfully updated.",
    processing:
      "Payment processing. Your Read plan will be upgraded when your payment completes.",
  },
  error: {
    default:
      "Oops! Something went wrong and your Read plan could not be updated. Please confirm your billing details or try again later.",
    requires_payment_method: PAYMENT_FAILED_ERROR_MESSAGE,
    payment_failed: PAYMENT_FAILED_ERROR_MESSAGE,
  },
};

const CREDIT_PURCHASE_MESSAGE_BY_OPTS: MessageConfig = {
  success: {
    default: "Success! Your credits have been added to your account.",
  },
  error: {
    default:
      "Oops! Something went wrong and your credits could not be applied to your account. Please confirm your billing details or try again later.",
    requires_payment_method: PAYMENT_FAILED_ERROR_MESSAGE,
    payment_failed: PAYMENT_FAILED_ERROR_MESSAGE,
  },
};

const UPDATE_MESSAGE_BY_OPTS: MessageConfig = {
  success: {
    default: "Your billing information has been successfully updated.",
  },
  error: {
    default:
      "Oops! Something went wrong and your billing information could not be updated. Please confirm your billing details or try again later.",
  },
};

const OPTIONS_BY_SOURCE: { [key in UpdateSource]: MessageConfig } = {
  subscription_purchase: SUBSCRIPTION_PURCHASE_MESSAGE_BY_OPTS,
  credit_purchase: CREDIT_PURCHASE_MESSAGE_BY_OPTS,
  update: UPDATE_MESSAGE_BY_OPTS,
};

const BillingUpdateStatusBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();

  // should be either "subscription_purchase" (if coming from the subscription checkout flow),
  // "credit_purchase" (if coming from the credit checkout flow), or "update" (if coming from
  // billing info update)
  const fromParam = queryParams.get("from") as UpdateSource;

  // will be either "true" or "false"
  const successParam = queryParams.get("success");

  // if either one of these params isn't defined, we don't know what message to show, so show nothing.
  if (!fromParam || !successParam) return null;

  const severity = successParam === "true" ? "success" : "error";
  const options = OPTIONS_BY_SOURCE[fromParam];
  const reason = queryParams.get("reason") ?? "default";

  const handleDismiss = () => {
    navigate(
      withoutQueryParams(location.pathname, queryParams, [
        "success",
        "reason",
        "from",
      ]),
      { replace: true }
    );
  };

  return (
    <Alert
      autoDismissTimeout={severity === "success" ? 5000 : 10000}
      onDismiss={handleDismiss}
      severity={severity}
    >
      {options[severity][reason] ?? options[severity].default}
    </Alert>
  );
};

export default BillingUpdateStatusBanner;
